import React, { useState, useRef } from "react"
import {
  Editor,
  EditorState,
  RichUtils,
  AtomicBlockUtils,
  convertToRaw,
} from "draft-js"
import "draft-js/dist/Draft.css"
import {
  Container,
  TextField,
  Button,
  Grid,
  Typography,
  Toolbar,
  IconButton,
  Box,
  Snackbar,
  Alert,
} from "@mui/material"
import {
  AddCircleOutline,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Delete,
} from "@mui/icons-material"
import ImageIcon from "@mui/icons-material/Image"
import TagSelector from "./tagSelector"
import { db } from "../firebase" //need to keep this here to initialize firebase
import { collection, addDoc, serverTimestamp } from "firebase/firestore"
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage"

const RecipeBuilder = () => {
  const [title, setTitle] = useState("")
  const [selectedTags, setSelectedTags] = useState([])
  const [thumbnail, setThumbnail] = useState(null)
  const [ingredients, setIngredients] = useState([""])
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")
  const [editorStates, setEditorStates] = useState([EditorState.createEmpty()])
  const fileInputRefs = useRef([])

  const storage = getStorage()

  const handleIngredientChange = (index, event) => {
    const newIngredients = [...ingredients]
    newIngredients[index] = event.target.value
    setIngredients(newIngredients)
  }

  const addIngredientField = () => {
    setIngredients([...ingredients, ""])
  }

  const deleteIngredientField = (index) => {
    const newIngredients = ingredients.filter((_, i) => i !== index)
    setIngredients(newIngredients)
  }

  const handleEditorChange = (index, editorState) => {
    const newEditorStates = [...editorStates]
    newEditorStates[index] = editorState
    setEditorStates(newEditorStates)
  }

  const handleKeyCommand = (index, command) => {
    const newState = RichUtils.handleKeyCommand(editorStates[index], command)
    if (newState) {
      handleEditorChange(index, newState)
      return "handled"
    }
    return "not-handled"
  }

  const handlePastedFiles = (index, files) => {
    const file = files[0]
    if (file.type.startsWith("image/")) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const contentState = editorStates[index].getCurrentContent()
        const contentStateWithEntity = contentState.createEntity(
          "IMAGE",
          "IMMUTABLE",
          { src: e.target.result }
        )
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
        const newEditorState = AtomicBlockUtils.insertAtomicBlock(
          editorStates[index],
          entityKey,
          " "
        )
        handleEditorChange(index, newEditorState)
      }
      reader.readAsDataURL(file)
      return "handled"
    }
    return "not-handled"
  }

  const toggleInlineStyle = (index, style) => {
    handleEditorChange(
      index,
      RichUtils.toggleInlineStyle(editorStates[index], style)
    )
  }

  const handleImageUpload = async (index, e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith("image/")) {
      const storageRef = ref(storage, `images/${file.name}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)

      const contentState = editorStates[index].getCurrentContent()
      const contentStateWithEntity = contentState.createEntity(
        "IMAGE",
        "IMMUTABLE",
        { src: downloadURL } // Set the URL instead of Base64
      )
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(
        editorStates[index],
        entityKey,
        " "
      )
      handleEditorChange(index, newEditorState)
    }
  }

  const handleImageInsert = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].click()
    }
  }

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0]
    if (file && file.type.startsWith("image/")) {
      const storageRef = ref(storage, `thumbnails/${file.name}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)
      setThumbnail(downloadURL) // Set the URL instead of Base64
    }
  }

  const imageBlockRenderer = (contentBlock) => {
    if (contentBlock.getType() === "atomic") {
      return {
        component: ImageComponent,
        editable: false,
      }
    }
    return null
  }

  const ImageComponent = (props) => {
    const entity = props.contentState.getEntity(props.block.getEntityAt(0))
    const { src } = entity.getData()
    return (
      <img src={src} alt="" style={{ maxWidth: "100%", margin: "10px 0" }} />
    )
  }

  //request backend to save recipe data in mongoDB (excluding images, still in firebase blob storage for now)
  const saveRecipe = async () => {
    const rawContent = editorStates.map((editorState) =>
      convertToRaw(editorState.getCurrentContent())
    )

    const recipeData = {
      title,
      selectedTags,
      thumbnail,
      ingredients,
      instructions: rawContent,
      timestamp: new Date(),
    }

    try {
      // Send the recipe data to the backend server
      const response = await fetch("/api/recipes", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(recipeData),
      })

      if (response.ok) {
        setSnackbarMessage("Recipe successfully saved!")
        setSnackbarSeverity("success")
      } else {
        setSnackbarMessage("Error saving recipe: " + response.statusText)
        setSnackbarSeverity("error")
      }
    } catch (e) {
      setSnackbarMessage("Error saving recipe: " + e.message)
      setSnackbarSeverity("error")
    } finally {
      setSnackbarOpen(true)
    }
  }

  const handleSnackbarClose = () => {
    setSnackbarOpen(false)
  }

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Typography variant="h3">Create Recipe</Typography>
      <Typography variant="h6" sx={{ mt: 3, fontSize: "2rem" }}>
        Recipe Title
      </Typography>
      <TextField
        fullWidth
        label="Recipe Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 2 }}
        style={{ width: "30%" }}
      />
      <Typography variant="h6" sx={{ mt: 3, fontSize: "2rem" }}>
        Select Up To 3 Tags
      </Typography>
      <TagSelector
        selectedTags={selectedTags}
        setSelectedTags={setSelectedTags}
      />
      <Typography variant="h6" sx={{ mt: 3, fontSize: "2rem" }}>
        Recipe Thumbnail
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" component="label" color="primary">
          Upload Thumbnail
          <input type="file" hidden onChange={handleThumbnailUpload} />
        </Button>
        {thumbnail && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Thumbnail Preview:</Typography>
            <img
              src={thumbnail}
              alt="Thumbnail"
              style={{ maxWidth: "100%", height: "auto", marginTop: "10px" }}
            />
          </Box>
        )}
      </Box>
      <Typography variant="h6" sx={{ mt: 3, fontSize: "2rem" }}>
        Ingredients with Measurements
      </Typography>
      {ingredients.map((ingredient, index) => (
        <Grid container spacing={2} key={index} alignItems="center">
          <Grid item xs={10}>
            <TextField
              fullWidth
              label={`Ingredient ${index + 1}`}
              value={ingredient}
              onChange={(e) => handleIngredientChange(index, e)}
              style={{ paddingTop: "5px" }}
            />
          </Grid>
          <Grid item xs={2}>
            <IconButton
              color="secondary"
              onClick={() => deleteIngredientField(index)}
            >
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<AddCircleOutline />}
        onClick={addIngredientField}
        sx={{ mt: 2 }}
      >
        Add Ingredient
      </Button>

      <Typography variant="h6" sx={{ mt: 3, fontSize: "2rem" }}>
        Instructions
      </Typography>
      {editorStates.map((editorState, index) => (
        <Grid container spacing={2} key={index}>
          <Grid item xs={10}>
            <div
              style={{
                border: "1px solid #ccc",
                padding: "10px",
                minHeight: "50px",
                borderRadius: "4px",
                //marginBottom: "10px",
                backgroundColor: "#fafafa",
                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              }}
            >
              <Toolbar
                style={{
                  borderBottom: "1px solid #ddd",
                  marginBottom: "10px",
                }}
              >
                <IconButton
                  onMouseDown={(e) => {
                    e.preventDefault()
                    toggleInlineStyle(index, "BOLD")
                  }}
                  style={{
                    color: editorStates[index]
                      .getCurrentInlineStyle()
                      .has("BOLD")
                      ? "#3f51b5"
                      : "#000",
                  }}
                >
                  <FormatBold />
                </IconButton>
                <IconButton
                  onMouseDown={(e) => {
                    e.preventDefault()
                    toggleInlineStyle(index, "ITALIC")
                  }}
                  style={{
                    color: editorStates[index]
                      .getCurrentInlineStyle()
                      .has("ITALIC")
                      ? "#3f51b5"
                      : "#000",
                  }}
                >
                  <FormatItalic />
                </IconButton>
                <IconButton
                  onMouseDown={(e) => {
                    e.preventDefault()
                    toggleInlineStyle(index, "UNDERLINE")
                  }}
                  style={{
                    color: editorStates[index]
                      .getCurrentInlineStyle()
                      .has("UNDERLINE")
                      ? "#3f51b5"
                      : "#000",
                  }}
                >
                  <FormatUnderlined />
                </IconButton>
                <IconButton
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleImageInsert(index)
                  }}
                >
                  <ImageIcon />
                </IconButton>
              </Toolbar>
              <Editor
                editorState={editorState}
                onChange={(newState) => handleEditorChange(index, newState)}
                handleKeyCommand={(command) => handleKeyCommand(index, command)}
                blockRendererFn={imageBlockRenderer}
                handlePastedFiles={(files) => handlePastedFiles(index, files)}
              />
              <input
                type="file"
                ref={(el) => (fileInputRefs.current[index] = el)}
                style={{ display: "none" }}
                onChange={(e) => handleImageUpload(index, e)}
              />
            </div>
          </Grid>
        </Grid>
      ))}
      <Button
        variant="contained"
        color="primary"
        onClick={saveRecipe}
        sx={{ mt: 2 }}
      >
        Submit Recipe
      </Button>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default RecipeBuilder
